/** ভোটার নিবন্ধন ফরম (ফরম-২, বিধি-১০) — data helpers. */

export const INITIAL_VOTER_REGISTRATION_FORM = {
    // masthead + ১-২
    formNo: '',
    voterAreaNo: '',
    voterAreaName: '',
    // ৩-৫
    nameBn: '',
    nameEn: '',
    fatherName: '',
    // ৬-৭
    fatherNid: '',
    fatherNidNa: false,
    fatherVoterNo: '',
    fatherDeathYear: '',
    // ৮-১০
    motherName: '',
    motherNid: '',
    motherNidNa: false,
    motherVoterNo: '',
    motherDeathYear: '',
    // ১১-১৩ — the same স্বামী / স্ত্রী choice runs down all three
    spouseKind: '',
    spouseName: '',
    spouseNameNa: false,
    spouseNid: '',
    spouseNidNa: false,
    spouseDeathYear: '',
    spouseDeathNa: false,
    // ১৪-১৬
    birthRegNo: '',
    birthDate: '',
    birthPlace: '',
    // ১৭-২৩
    gender: '',
    maritalStatus: '',
    education: '',
    occupation: '',
    disability: '',
    disabilityOther: '',
    identificationMark: '',
    bloodGroup: '',
    // ২৪ — current address
    curDivision: '',
    curDistrict: '',
    curUpazila: '',
    curLocalBody: '',
    curWard: '',
    curMouza: '',
    curVillage: '',
    curHolding: '',
    curPostOffice: '',
    curPostCode: '',
    curRmo: '',
    // ২৫ — permanent address
    permDivision: '',
    permDistrict: '',
    permUpazila: '',
    permLocalBody: '',
    permWard: '',
    permMouza: '',
    permVillage: '',
    permHolding: '',
    permPostOffice: '',
    permPostCode: '',
    permRmo: '',
    // ২৬-৩১
    tin: '',
    drivingLicense: '',
    passport: '',
    telephone: '',
    mobile: '',
    religion: '',
    religionOther: '',
    // ৩২, ৩৪
    excludedReason: '',
    identifierNid: '',
    collectorNid: '',
};

/** The option sets the form prints as tick boxes, in its own order. */
export const SPOUSE_KINDS = ['husband', 'wife'];
export const GENDERS = ['male', 'female'];
export const MARITAL_STATUSES = ['unmarried', 'married', 'divorced', 'widow', 'widower'];
export const DISABILITIES = ['blind', 'physical', 'deaf', 'mute', 'other'];
export const BLOOD_GROUPS = ['aPlus', 'aMinus', 'bPlus', 'bMinus', 'abPlus', 'abMinus', 'oPlus', 'oMinus'];
export const RMO_TYPES = ['rural', 'municipality', 'city', 'other', 'cityCorporation'];
export const RELIGIONS = ['islam', 'hindu', 'christian', 'buddhist', 'other'];

export function displayField(value) {
    const v = String(value ?? '').trim();
    return v || '';
}

/** Convert Bengali/Arabic-Indic digits to ASCII — used only for validation logic. */
function toAsciiDigits(str) {
    return str
        .replace(/[০-৯]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x09e6 + 48))
        .replace(/[٠-٩]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48));
}

const BENGALI_DIGITS = '০১২৩৪৫৬৭৮৯';

/** The form is printed in Bengali, so its numbers are written in Bengali too. */
export function toBengaliDigits(value) {
    return String(value ?? '').replace(/\d/g, (digit) => BENGALI_DIGITS[Number(digit)]);
}

/**
 * A date as item ১৫ takes it: দিন-মাস-বছর, one digit per box under the printed
 * দিন / মাস / বছর headings.
 * @param {Date | null} date
 */
export function formatDateField(date) {
    if (!date || Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return toBengaliDigits(`${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`);
}

/**
 * Read a stored date back for the picker. Bengali or ASCII digits are both
 * accepted, and -, / or . as the separator.
 * @returns {Date | null}
 */
export function parseDateField(value) {
    const match = toAsciiDigits(String(value ?? '').trim())
        .match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    if (!match) return null;
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const date = new Date(year, month - 1, day);
    // Reject a date the calendar rolled over, e.g. 31-02-2020.
    if (date.getDate() !== day || date.getMonth() !== month - 1) return null;
    return date;
}

/** Keep only digit characters (ASCII 0-9, Bengali ০-৯, Arabic-Indic ٠-٩). */
export function normalizeDigits(value, maxLength) {
    const digits = String(value ?? '').replace(/[^\d০-৯٠-٩]/g, '');
    return maxLength ? digits.slice(0, maxLength) : digits;
}

/** Strip to digits, drop a leading 880 country code if present. */
export function normalizeBdPhone(value) {
    let digits = String(value ?? '').replace(/[^\d০-৯٠-٩]/g, '');
    const ascii = toAsciiDigits(digits);
    if (ascii.startsWith('880') && ascii.length >= 13) digits = digits.slice(3);
    return digits.slice(0, 11);
}

/** Every Bangladeshi telephone or mobile number: 11 digits starting with 0. */
export function isValidBdPhone(value) {
    return /^0\d{10}$/.test(toAsciiDigits(normalizeBdPhone(value)));
}

export function getMobileValidationError(value, t) {
    const digits = normalizeBdPhone(value);
    if (!digits) return '';
    if (digits.length < 11) {
        return t('voterRegistrationForm.validation.mobileLength', { count: digits.length });
    }
    if (!isValidBdPhone(digits)) return t('voterRegistrationForm.validation.mobileInvalid');
    return '';
}

/** A national ID is 10, 13 or 17 digits; anything else is a typo worth flagging. */
export function getNidValidationError(value, t) {
    const digits = normalizeDigits(value, 17);
    if (!digits) return '';
    if (![10, 13, 17].includes(digits.length)) {
        return t('voterRegistrationForm.validation.nidLength');
    }
    return '';
}

/** Turn a stored value into the digits a comb row prints, one per cell. */
export function combDigits(value, cellCount) {
    const digits = String(value ?? '').replace(/[^\d০-৯٠-٩]/g, '');
    return toBengaliDigits(digits).slice(0, cellCount);
}

const FORM_SNAPSHOT_KEY = 'voter-registration-form-last-snapshot';

/**
 * What the registration centre will not take the sheet without. Everything the
 * form itself marks conditional — the guardian death years, the spouse block,
 * TIN / licence / passport, the বাদপড়া reason — stays optional here too.
 */
const REQUIRED_FIELDS = [
    'nameBn',
    'nameEn',
    'fatherName',
    'motherName',
    'birthDate',
    'birthPlace',
    'gender',
    'maritalStatus',
    'curDivision',
    'curDistrict',
    'curUpazila',
    'permDivision',
    'permDistrict',
    'permUpazila',
    'mobile',
];

/** @returns {typeof INITIAL_VOTER_REGISTRATION_FORM | null} */
export function getVoterRegistrationFormSnapshot() {
    if (typeof window === 'undefined') return null;
    try {
        const raw = localStorage.getItem(FORM_SNAPSHOT_KEY);
        if (!raw) return null;
        return { ...INITIAL_VOTER_REGISTRATION_FORM, ...JSON.parse(raw) };
    } catch {
        return null;
    }
}

/** Save the latest sheet so it can be restored later. */
export function saveVoterRegistrationFormSnapshot(form) {
    if (typeof window === 'undefined' || !form) return;
    try {
        const payload = {};
        for (const key of Object.keys(INITIAL_VOTER_REGISTRATION_FORM)) {
            const initial = INITIAL_VOTER_REGISTRATION_FORM[key];
            payload[key] = typeof initial === 'boolean' ? Boolean(form[key]) : displayField(form[key]);
        }
        localStorage.setItem(FORM_SNAPSHOT_KEY, JSON.stringify(payload));
    } catch {
        /* ignore */
    }
}

/**
 * @param {typeof INITIAL_VOTER_REGISTRATION_FORM} form
 * @param {(key: string, opts?: object) => string} t
 * @returns {{ field: string, message: string }[]}
 */
export function getVoterRegistrationFormValidationErrors(form, t) {
    const errors = [];

    for (const key of REQUIRED_FIELDS) {
        if (!displayField(form?.[key])) {
            errors.push({
                field: key,
                message: t('voterRegistrationForm.validation.required', {
                    field: t(`voterRegistrationForm.fields.${key}`),
                }),
            });
        }
    }

    for (const key of ['telephone', 'mobile']) {
        const err = getMobileValidationError(form?.[key], t);
        if (err) errors.push({ field: key, message: err });
    }

    for (const key of ['fatherNid', 'motherNid', 'spouseNid', 'identifierNid', 'collectorNid']) {
        const err = getNidValidationError(form?.[key], t);
        if (err) errors.push({ field: key, message: err });
    }

    return errors;
}

export function isVoterRegistrationFormComplete(form, t) {
    return getVoterRegistrationFormValidationErrors(form, t).length === 0;
}

/**
 * Every blank still empty, named the way the form asks for it and in the order
 * it asks — what the download confirmation lists back to the applicant. Ticks
 * are choices rather than blanks, so they are counted only when nothing at all
 * is chosen.
 *
 * @param {typeof INITIAL_VOTER_REGISTRATION_FORM} form
 * @param {(key: string, opts?: object) => string} t
 * @returns {string[]}
 */
export function getEmptyVoterRegistrationFields(form, t) {
    return Object.keys(INITIAL_VOTER_REGISTRATION_FORM)
        .filter((key) => typeof INITIAL_VOTER_REGISTRATION_FORM[key] !== 'boolean')
        .filter((key) => !displayField(form?.[key]))
        .map((key) => t(`voterRegistrationForm.fields.${key}`));
}

/** `aPlus` → `bloodAPlus`, the key the printed tick box carries. */
const tickKey = (prefix, option) => `${prefix}${option.charAt(0).toUpperCase()}${option.slice(1)}`;

/**
 * Everything the preview prints: the trimmed answers, plus one boolean per
 * printed tick box. The boxes are derived rather than stored, so a radio group
 * stays a single field in the form state and the sheet still gets its ✓ in the
 * right square.
 */
export function buildVoterRegistrationSheet(form) {
    const out = {};
    for (const key of Object.keys(INITIAL_VOTER_REGISTRATION_FORM)) {
        const initial = INITIAL_VOTER_REGISTRATION_FORM[key];
        out[key] = typeof initial === 'boolean' ? Boolean(form?.[key]) : displayField(form?.[key]);
    }

    const groups = [
        ['spouseKind', SPOUSE_KINDS, out.spouseKind],
        ['gender', GENDERS, out.gender],
        ['marital', MARITAL_STATUSES, out.maritalStatus],
        ['disability', DISABILITIES, out.disability],
        ['blood', BLOOD_GROUPS, out.bloodGroup],
        ['curRmo', RMO_TYPES, out.curRmo],
        ['permRmo', RMO_TYPES, out.permRmo],
        ['religion', RELIGIONS, out.religion],
    ];
    for (const [prefix, options, chosen] of groups) {
        for (const option of options) out[tickKey(prefix, option)] = chosen === option;
    }
    // ৩১'s last box is a tick beside its own "other" blank, so it carries a
    // different key from the blank itself.
    out.religionOtherTick = out.religion === 'other';

    return out;
}

/** @param {{ field: string, message: string }[]} errors */
export function validationErrorsToFieldMap(errors) {
    const map = {};
    for (const { field, message } of errors) map[field] = message;
    return map;
}
